import { useEffect, useMemo, useState } from "react";

import { loadPasswordStrengthEstimator, type PasswordStrengthEstimator } from "@/lib/zxcvbn";

/** これ未満のスコアは弱いパスワードとして送信させない（zxcvbn の 0〜4） */
const MIN_PASSWORD_STRENGTH_SCORE = 3;

/** サービス名を含むパスワードを低く評価するための語 */
const SERVICE_NAME = "chillnn";

const WEAK_PASSWORD_ERROR_MESSAGE =
	"推測されやすいパスワードです。単語を組み合わせるなどして、推測されにくいものにしてください";

type Params = {
	password: string;
	email: string;
	/** zod の形式チェック（文字数・文字種）のエラー */
	formatErrorMessage: string | undefined;
};

/**
 * 新しく作るパスワードについて、表示するエラーと送信できるかを返す
 * - 形式のエラーを優先し、形式を満たしたうえで推測されやすいときに強度のエラーを出す
 * - 強度の判定関数の読み込み中は送信させない
 * - 読み込みに失敗したときは強度判定をせずに通す（形式チェックは効いている）
 */
export const useNewPasswordError = ({ password, email, formatErrorMessage }: Params) => {
	const [estimator, setEstimator] = useState<PasswordStrengthEstimator | undefined>(undefined);
	const [hasLoadFailed, setHasLoadFailed] = useState(false);

	useEffect(() => {
		let isMounted = true;

		loadPasswordStrengthEstimator()
			.then((loaded) => {
				// 関数をそのまま渡すと更新関数として呼ばれてしまうため、関数を返す関数で包む
				if (isMounted) setEstimator(() => loaded);
			})
			.catch(() => {
				if (isMounted) setHasLoadFailed(true);
			});

		return () => {
			isMounted = false;
		};
	}, []);

	const strengthErrorMessage = useMemo(() => {
		if (estimator === undefined || password === "") return undefined;

		const emailLocalPart = email.split("@").at(0) ?? "";
		const userInputs = [email, emailLocalPart, SERVICE_NAME].filter((input) => input !== "");

		return estimator(password, userInputs) < MIN_PASSWORD_STRENGTH_SCORE ? WEAK_PASSWORD_ERROR_MESSAGE : undefined;
	}, [estimator, password, email]);

	const errorMessage = formatErrorMessage ?? strengthErrorMessage;
	const isChecking = estimator === undefined && !hasLoadFailed;

	return {
		errorMessage,
		isSubmittable: !isChecking && errorMessage === undefined,
	};
};
